import { useHistory, useLocation } from 'react-router-dom';
import * as React from 'react';

import { DialogRoute, mergeQueryString, updateSearch } from 'bernie-core';
import { Layer } from 'bernie-view';

import { ToolbarHeadingLevel, ToolbarType, UitkToolbar } from 'uitk-react-toolbar';
import { UitkDialogContent, UitkFullscreenDialog } from 'uitk-react-dialog';
import { UitkScrim, UitkScrimType } from 'uitk-react-scrim';

import { UitkSpacing } from 'uitk-react-spacing';
import { ActivitySort } from '../../../common/__generated__/api';

import { PrimaryButtonTracked, TertiaryButtonTracked } from '../../../utils/click-trackers';
import { useStores } from '../../../utils/hooks';

import { getFilteredObjectKeys, getPageId } from '../../../helpers/common-helper';

import { getUpdatedFilterQueryObject } from './filter-helper';
import { SortFilter } from './sort-filter';
import { SortFilterDialogButton } from './sort-filter-dialog-button';

export interface SortFilterDialogProps {
  loading?: boolean;
}

export const SortFilterDialog: React.FC<SortFilterDialogProps> = ({ loading }): JSX.Element => {
  const { analytics, activitySearchStore } = useStores();
  const history = useHistory();
  const location = useLocation();
  const pageId = getPageId(activitySearchStore);
  const focusField: React.RefObject<any> = React.useRef();
  const { toolbar, applyAction } = activitySearchStore.universalSortAndFilter || {};
  const { primary, secondaries } = toolbar?.actions || {};

  const handleSortAndFilterDialogClose = (): void => {
    updateSearch({
      history: history,
      location: location,
      newParams: { pwaDialog: '' }
    });
  };

  const createQueryString = (filterKeys: any[], pwaDialog: any): string =>
    mergeQueryString(location.search, {
      sort: activitySearchStore.sortAndFilterObject.initialSelectedSort,
      slimSearchKeyword: activitySearchStore.sortAndFilterObject.keywordValue,
      pwaDialog: pwaDialog,
      ...getUpdatedFilterQueryObject(activitySearchStore)
    });

  const handleClick = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const filterKeys = getFilteredObjectKeys(activitySearchStore.sortAndFilterObject.filters);

    const newQueryString = createQueryString(filterKeys, '');

    if (activitySearchStore.sortAndFilterObject.keywordValue.length > 0) {
      analytics.trackEvent(
        `${pageId}.filterSort.SearchActivity`,
        'keyword-search-done',
        analytics.omnitureData,
        null,
        true
      );
    }

    updateSearch({
      history: history,
      location: location,
      newParams: newQueryString,
      newState: {},
      replace: true
    });
  };

  const clearForm = (): void => {
    activitySearchStore.sortAndFilterObject.initialSelectedSort = ActivitySort.RECOMMENDED;
    activitySearchStore.sortAndFilterObject.keywordValue = '';
    const filterCategoryKeys = getFilteredObjectKeys(activitySearchStore.sortAndFilterObject.filters);
    filterCategoryKeys.forEach((filterCategoryKey) => {
      const filterKeys = getFilteredObjectKeys(activitySearchStore.sortAndFilterObject.filters[filterCategoryKey]);
      filterKeys.forEach(
        (filterKey) => (activitySearchStore.sortAndFilterObject.filters[filterCategoryKey][filterKey].selected = false)
      );
    });
    activitySearchStore.sortAndFilterObject.initialSeeAllOption = false;

    const newQueryString = createQueryString(filterCategoryKeys, 'filter');
    updateSearch({
      history: history,
      location: location,
      newParams: newQueryString,
      newState: {},
      replace: true
    });
  };

  return (
    <fieldset disabled={loading} aria-busy={loading} ref={focusField} data-testid="sort-filter-dialog--fieldset">
      <DialogRoute id="filter">
        <Layer id="dialog-filter">
          <UitkFullscreenDialog
            data-testid="sort-filter-dialog--fullscreen"
            closeCallback={handleSortAndFilterDialogClose}
            dialogShow
            strictFullscreen
            returnFocusOnClose
          >
            <UitkToolbar
              key="sort-filter-dialog--toolbar"
              action={(): void => handleSortAndFilterDialogClose()}
              type={ToolbarType.CLOSE}
              iconLabel={primary?.accessibility}
              headingLevel={ToolbarHeadingLevel.TWO}
              actionContent={
                <TertiaryButtonTracked
                  key="sort-filter-dialog-clear-button"
                  type="reset"
                  onClick={clearForm}
                  rfrr={secondaries && secondaries[0]?.action?.analytics.referrerId}
                  linkName={secondaries && secondaries[0]?.action?.analytics.linkName}
                  data-testid="sort-filter-dialog--clear-button"
                  aria-label={secondaries && secondaries[0]?.accessibility}
                >
                  {secondaries && secondaries[0]?.primary}
                </TertiaryButtonTracked>
              }
              header={toolbar?.primary}
            />
            <UitkSpacing margin={{ blockend: 'twelve' }}>
              <UitkDialogContent key="sort-filter-dialog-content">
                <SortFilter inDialog />
              </UitkDialogContent>
            </UitkSpacing>
            <UitkScrim key="sort-filter-dialog-scrim" type={UitkScrimType.BUTTON}>
              <PrimaryButtonTracked
                data-testid="sort-filter-dialog--done-button"
                linkName={applyAction?.action.analytics.linkName}
                rfrr={applyAction?.action.analytics.referrerId}
                type="button"
                onClick={handleClick}
                isFullWidth
                aria-label={applyAction?.accessibility}
              >
                {applyAction?.primary}
              </PrimaryButtonTracked>
            </UitkScrim>
          </UitkFullscreenDialog>
        </Layer>
      </DialogRoute>
      <SortFilterDialogButton pageId={pageId} />
    </fieldset>
  );
};
